import React, { useEffect, useState, useRef, useCallback } from "react";
import { getAll } from "../api/API";
import ResourceFilters from "../components/resource/ResoruceFilters";
import ResourceCard from "../components/resource/ResourceCard";
import "../styles/Resources.css";
import Seo from "../components/generic/Seo";

const LIMIT = 10;

const ResourcePage = () => {
  const [resources, setResources] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const observerRef = useRef(null);
  const resultsRef = useRef(null);

  /* ---------------- LOAD RESOURCES ---------------- */
  const loadMoreResources = async () => {
    if (!hasMore) return;

    const res = await getAll(`/resources?limit=${LIMIT}&page=${page}`);

    if (res.docs.length < LIMIT) {
      setHasMore(false);
    }

    // ✅ Deduplicate by ID (StrictMode-safe)
    setResources((prev) => {
      const map = new Map(prev.map((r) => [r.id, r]));
      res.docs.forEach((r) => map.set(r.id, r));
      return Array.from(map.values());
    });
  };

  useEffect(() => {
    loadMoreResources();
  }, [page]);

  /* ---------------- INFINITE SCROLL ---------------- */
  const lastResourceRef = useCallback(
    (node) => {
      if (!hasMore) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [hasMore],
  );

  /* ---------------- APPLY FILTERS ---------------- */
  useEffect(() => {
    let data = resources;

    if (selectedCategory) {
      data = data.filter((r) => r.category === selectedCategory);
    }

    if (selectedTags.length) {
      data = data.filter((r) =>
        r.tags?.some((t) => selectedTags.includes(t.tag)),
      );
    }

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      data = data.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q),
      );
    }

    setFiltered(data);
  }, [resources, selectedCategory, selectedTags, searchText]);

  /* ---------------- RESET FILTERS ---------------- */
  const resetFilters = () => {
    setSelectedCategory("");
    setSelectedTags([]);
    setSearchText("");
    setFiltered(resources);
  };

  /* ---------------- MOBILE SCROLL ---------------- */
  const scrollToResults = () => {
    resultsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const objSeo = {
    title: "Springevening Private Limited",
    description:
      "Explore guides, documentation, case studies, and learning resources curated by Springevening.",
    keywords:
      "software resources, ERP documentation, developer guides, Springevening resources",
    pathSlug: "/resources",
  };

  return (
    <>
      <Seo data={objSeo} />
      <div className="resource-wrapper">
        <div className="row">
          {/* LEFT FILTERS */}
          <div className="col-md-5 resource-left-section">
            <h1 className="resource-title text-secondary">
              Find the Perfect Learning Resource
            </h1>

            <p className="resource-description text-secondary">
              Explore insights across key areas—filter by tags or search
              instantly
            </p>

            <ResourceFilters
              resources={resources}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
              resetFilters={resetFilters}
              searchText={searchText}
              setSearchText={setSearchText}
            />

            {/* MOBILE SHOW RESULTS BUTTON */}
            <button
              className="result-button btn-primary w-80 d-md-none mt-3"
              onClick={scrollToResults}
            >
              Show results
            </button>
          </div>

          {/* RIGHT RESOURCE LIST */}
          <div className="col-md-7 right-section" ref={resultsRef}>
            <div className="card-column">
              {filtered.length === 0 ? (
                <p className="text-center text-secondary py-4">
                  No resources were found.
                </p>
              ) : (
                <>
                  {filtered.map((item, index) => {
                    const isLast = index === filtered.length - 1;

                    if (isLast) {
                      return (
                        <div ref={lastResourceRef} key={item.id}>
                          <ResourceCard item={item} />
                        </div>
                      );
                    }

                    return <ResourceCard key={item.id} item={item} />;
                  })}

                  {!hasMore && (
                    <p className="text-center text-secondary py-3">
                      End of resources.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResourcePage;
